import { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import ContestantHeader from '../components/ContestantHeader';
import '../pages/contestant/contestant.css';

function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

export default function HeatSelection() {
  const [heats, setHeats]       = useState([]);
  const [songs, setSongs]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [saving, setSaving]     = useState({}); // { [songId]: true }
  const [saved, setSaved]       = useState({}); // { [songId]: true }
  const [saveError, setSaveError] = useState({}); // { [songId]: msg }

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [heatsRes, songsRes] = await Promise.all([
        api.get('/api/heat-selection/heats'),
        api.get('/api/heat-selection/my-songs'),
      ]);
      setHeats(heatsRes.data.heats);
      setSongs(songsRes.data.songs);
    } catch {
      setError('Failed to load heat information. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  async function handleSelect(songId, heatId) {
    setSaving((p) => ({ ...p, [songId]: true }));
    setSaveError((p) => ({ ...p, [songId]: '' }));
    setSaved((p) => ({ ...p, [songId]: false }));
    try {
      const res = await api.post('/api/heat-selection/select', { song_id: songId, heat_id: heatId });
      setSongs((prev) => prev.map((s) => s.id === songId ? { ...s, ...res.data.song } : s));
      setSaved((p) => ({ ...p, [songId]: true }));
      // Reload heats to update slot counts
      const heatsRes = await api.get('/api/heat-selection/heats');
      setHeats(heatsRes.data.heats);
    } catch (err) {
      setSaveError((p) => ({ ...p, [songId]: err.response?.data?.error || 'Failed to save. Please try again.' }));
    } finally {
      setSaving((p) => ({ ...p, [songId]: false }));
    }
  }

  if (loading) {
    return (
      <>
        <ContestantHeader />
        <div className="c-loading"><div className="c-spinner" /></div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <ContestantHeader />
        <div style={{ maxWidth: '760px', margin: '2rem auto', padding: '0 1rem' }}>
          <div className="c-alert">{error}</div>
        </div>
      </>
    );
  }

  return (
    <>
      <ContestantHeader />
      <div style={{ maxWidth: '900px', margin: '2rem auto', padding: '0 1rem' }}>
        <h1 style={{ fontSize: '1.6rem', fontWeight: 700, marginBottom: '0.4rem' }}>FOOM Heat Self-Selection</h1>
        <p style={{ color: '#6b7280', marginBottom: '2rem', fontSize: '0.95rem' }}>
          Please choose which heat you would like to perform in. The earlier you select, the more likely you are to get your preferred heat.
        </p>

        {songs.length === 0 ? (
          <div style={{
            background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px',
            padding: '2rem', textAlign: 'center', color: '#6b7280',
          }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem' }}>No eligible songs found</div>
            <div style={{ fontSize: '0.9rem' }}>
              Only paid, submitted FOOM Live songs are shown here. If you think this is an error, please contact the organisers.
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>

            {/* Left: heat cards */}
            <div style={{ flex: '0 0 280px', minWidth: '240px' }}>
              <h2 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.75rem', color: '#374151' }}>Available Heats</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {heats.map((h) => {
                  const isFull = h.max_performers !== null && h.slots_taken >= h.max_performers;
                  const slotsLeft = h.max_performers !== null ? Math.max(0, h.max_performers - h.slots_taken) : null;
                  return (
                    <div
                      key={h.heat_id}
                      style={{
                        background: isFull ? '#fef2f2' : '#fff',
                        border: `1px solid ${isFull ? '#fca5a5' : '#e5e7eb'}`,
                        borderRadius: '10px',
                        padding: '0.875rem 1rem',
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: '0.2rem' }}>
                        Heat {h.heat_number}{h.heat_name && h.heat_name !== `Heat ${h.heat_number}` ? ` — ${h.heat_name}` : ''}
                      </div>
                      {h.heat_date && (
                        <div style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '0.15rem' }}>
                          {formatDate(h.heat_date)}
                        </div>
                      )}
                      {h.performer_arrival_time && (
                        <div style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '0.15rem' }}>
                          Arrival: {h.performer_arrival_time.slice(0, 5)}
                        </div>
                      )}
                      {h.venue_name && (
                        <div style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '0.3rem' }}>
                          {h.venue_name}
                        </div>
                      )}
                      <div style={{
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: isFull ? '#dc2626' : '#16a34a',
                        marginTop: '0.35rem',
                      }}>
                        {isFull
                          ? 'This heat is full'
                          : slotsLeft !== null
                            ? `${slotsLeft} slot${slotsLeft !== 1 ? 's' : ''} left`
                            : 'Unlimited slots'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right: song preference panels */}
            <div style={{ flex: 1, minWidth: '280px' }}>
              <h2 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.75rem', color: '#374151' }}>Your Songs</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {songs.map((song) => {
                  const isLocked  = song.heat_preference_locked;
                  const isSaving  = saving[song.id];
                  const isSaved   = saved[song.id];
                  const errMsg    = saveError[song.id];

                  return (
                    <div
                      key={song.id}
                      style={{
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '10px',
                        padding: '1rem 1.125rem',
                      }}
                    >
                      <div style={{ fontWeight: 600, marginBottom: '0.2rem' }}>{song.title}</div>
                      {song.artist && (
                        <div style={{ fontSize: '0.83rem', color: '#9ca3af', marginBottom: '0.75rem' }}>{song.artist}</div>
                      )}

                      <label style={{ display: 'block', fontSize: '0.82rem', color: '#6b7280', marginBottom: '0.35rem', fontWeight: 600 }}>
                        Please choose a heat for this song
                      </label>

                      <select
                        disabled={isLocked || isSaving}
                        value={song.heat_preference_id || ''}
                        onChange={(e) => { if (e.target.value) handleSelect(song.id, e.target.value); }}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '0.875rem',
                          background: isLocked ? '#f9fafb' : '#fff',
                          color: '#111827',
                          cursor: isLocked ? 'not-allowed' : 'pointer',
                          opacity: isSaving ? 0.6 : 1,
                        }}
                      >
                        <option value="" disabled>Select a heat…</option>
                        {heats.map((h) => {
                          const isFull = h.max_performers !== null && h.slots_taken >= h.max_performers;
                          const alreadySelected = song.heat_preference_id === h.heat_id;
                          return (
                            <option
                              key={h.heat_id}
                              value={h.heat_id}
                              disabled={isFull && !alreadySelected}
                            >
                              {`Heat ${h.heat_number}`}{isFull && !alreadySelected ? ' — Full' : ''}
                            </option>
                          );
                        })}
                      </select>

                      <div style={{ marginTop: '0.5rem', minHeight: '1.2rem', fontSize: '0.82rem' }}>
                        {isSaving && (
                          <span style={{ color: '#6b7280' }}>Saving…</span>
                        )}
                        {!isSaving && (isLocked || isSaved) && !errMsg && (
                          <span style={{ color: '#16a34a', fontWeight: 600 }}>✓ Preference saved</span>
                        )}
                        {!isSaving && errMsg && (
                          <span style={{ color: '#dc2626' }}>{errMsg}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        )}
      </div>
    </>
  );
}
